import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

export interface ComboChartData {
  date: string
  spend: number
  blendedRoas: number
  paidMediaRoas?: number
}

interface ComboChartProps {
  title: string
  data: ComboChartData[]
  loading?: boolean
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`
  return `$${value.toFixed(0)}`
}

const formatDate = (date: string) => {
  const d = new Date(date)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    color: string
    dataKey: string
  }>
  label?: string
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (!active || !payload?.length) return null

  return (
    <div className="bg-tf-bg-card border border-tf-border rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-tf-text mb-2">{formatDate(label || '')}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 text-sm">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-tf-text-muted">{entry.name}:</span>
          <span className="font-medium text-tf-text">
            {entry.dataKey === 'spend' 
              ? formatCurrency(entry.value)
              : entry.value.toFixed(2)
            }
          </span>
        </div>
      ))}
    </div>
  )
}

export function ComboChart({ title, data, loading }: ComboChartProps) {
  if (loading) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
        <h3 className="text-sm font-semibold text-tf-text mb-4">{title}</h3>
        <div className="h-64 bg-tf-bg-muted rounded animate-pulse" />
      </div>
    )
  }

  if (!data.length) {
    return (
      <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
        <h3 className="text-sm font-semibold text-tf-text mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center text-tf-text-muted">
          No data available
        </div>
      </div>
    )
  }

  const maxSpend = Math.max(...data.map(d => d.spend)) * 1.1
  const maxRoas = Math.max(...data.map(d => Math.max(d.blendedRoas, d.paidMediaRoas || 0))) * 1.1

  return (
    <div className="bg-tf-bg-card rounded-xl border border-tf-border p-4">
      <h3 className="text-sm font-semibold text-tf-text mb-4">{title}</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={false}
            />
            <YAxis
              yAxisId="left"
              orientation="left"
              tickFormatter={formatCurrency}
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={false}
              tickLine={false}
              domain={[0, maxSpend]}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 11, fill: '#6b7280' }}
              axisLine={false}
              tickLine={false}
              domain={[0, maxRoas]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="square"
              iconSize={10}
              formatter={(value: string | number) => (
                <span className="text-xs text-tf-text-muted">{value}</span>
              )}
            />
            <Bar
              yAxisId="left"
              dataKey="spend"
              name="Spend"
              fill="#99d9d9"
              radius={[2, 2, 0, 0]}
              barSize={20}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="blendedRoas"
              name="Blended ROAS"
              stroke="#3b82f6"
              strokeWidth={2}
              dot={false}
            />
            {data.some(d => d.paidMediaRoas !== undefined) && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="paidMediaRoas"
                name="Paid Media ROAS"
                stroke="#8b5cf6"
                strokeWidth={2}
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default ComboChart
